import { useState } from 'react';
import { jobAPI } from '../../../services/api';

// Extracted verbatim from JobManagement.js (Task 4).
//
// `isLoading` stays owned by the orchestrator — this hook receives
// `setIsLoading` and flips it exactly as the original inline functions did.
//
// Coupling note (reported, not improvised around): the original `createJob`
// closed over `newJob` (form state), `activeTab`, `setShowCreateForm`,
// `resetJobForm()` (all orchestrator-owned) and `loadDashboardData` (owned
// by useJobDashboard, a sibling hook). None of that can be closed over once
// this code lives in its own module, so `createJob` now takes those as
// explicit parameters, supplied by the orchestrator at the single call site
// where it already has access to both hook instances. The internal
// try/catch/finally control flow and the order of operations (alert -> form
// reset -> conditional refresh) are unchanged.
// Several vehicle fields (year, mileage, ...) are optional integers in the
// API but the form sends '' when left blank, which 422s with int_parsing.
// Omit the key entirely when blank; coerce to a Number when present. Applies
// to every optional numeric vehicle field, not just one — a legitimate 0 is
// preserved.
const OPTIONAL_NUMERIC_VEHICLE_FIELDS = ['mileage', 'year'];

export const buildCreateJobPayload = (newJob) => {
  const vehicle = { ...newJob.vehicle };
  OPTIONAL_NUMERIC_VEHICLE_FIELDS.forEach((field) => {
    const value = vehicle[field];
    if (value === '' || value === null || value === undefined) {
      delete vehicle[field];
    } else {
      vehicle[field] = Number(value);
    }
  });
  return { ...newJob, vehicle };
};

export const useJobs = ({ setIsLoading }) => {
  const [jobs, setJobs] = useState([]);
  const [jobSearch, setJobSearch] = useState('');
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsTotalPages, setJobsTotalPages] = useState(1);

  const loadJobs = async (searchQuery = jobSearch) => {
    try {
      setIsLoading(true);
      const searchParams = {
        page: jobsPage,
        page_size: 20,
        sort_by: 'created_at',
        sort_order: 'desc'
      };

      // Add search term if provided
      if (searchQuery && searchQuery.trim()) {
        searchParams.search = searchQuery.trim();
      }

      const data = await jobAPI.searchJobs(searchParams);
      console.log('Loaded jobs:', data.jobs);
      setJobs(data.jobs || []);
      setJobsTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // See coupling note above re: activeTab/loadDashboardData/onSuccess params.
  const createJob = async (newJob, activeTab, loadDashboardData, onSuccess) => {
    try {
      setIsLoading(true);
      const createdJob = await jobAPI.createJob(buildCreateJobPayload(newJob));
      alert(`Job created successfully: ${createdJob.job_number}`);
      onSuccess();
      if (activeTab === 'jobs') loadJobs();
      if (activeTab === 'dashboard') loadDashboardData();
    } catch (error) {
      console.error('Error creating job:', error);
      alert(`Error creating job: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete job ${job.job_number}? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsLoading(true);
      await jobAPI.deleteJob(job.id);
      alert(`Job ${job.job_number} deleted successfully`);
      loadJobs(); // Refresh the jobs list
    } catch (error) {
      console.error('Error deleting job:', error);
      alert(`Error deleting job: ${error.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    jobs,
    jobSearch,
    setJobSearch,
    jobsPage,
    setJobsPage,
    jobsTotalPages,
    loadJobs,
    createJob,
    deleteJob
  };
};
